import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { glossaryService } from '../services/glossaryService';

const GlossaryPanel = ({ style, hideHeader = false }) => {
  const { palette } = useTheme();
  const [terms, setTerms] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTerm, setSelectedTerm] = useState(null);

  useEffect(() => {
    loadTerms();
  }, []);

  useEffect(() => {
    filterTerms();
  }, [searchQuery, terms]);

  const loadTerms = async () => {
    setLoading(true);
    const { terms: fetchedTerms, error } = await glossaryService.getAllTerms();
    if (!error) {
      setTerms(fetchedTerms);
      setFilteredTerms(fetchedTerms);
    }
    setLoading(false);
  };

  const filterTerms = () => {
    if (!searchQuery.trim()) {
      setFilteredTerms(terms);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = terms.filter(
      (term) =>
        term.term.toLowerCase().includes(query) ||
        term.definition.toLowerCase().includes(query) ||
        (term.category && term.category.toLowerCase().includes(query))
    );
    setFilteredTerms(filtered);
  };

  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const firstLetter = term.term[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(term);
    return acc;
  }, {});

  const sortedLetters = Object.keys(groupedTerms).sort();

  return (
    <View style={[styles.container, style]}>
      {!hideHeader && (
        <View style={styles.header}>
          <Text style={[styles.title, { color: palette.text || 'white' }]}>
            <Text>Business Glossary</Text>
          </Text>
          <Text style={[styles.subtitle, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
            <Text>Quick reference for business terms</Text>
          </Text>
        </View>
      )}

      {/* Search */}
      <View style={styles.searchContainer}>
        <TextInput
          style={[styles.searchInput, { borderColor:'rgba(255, 255, 255, 0.8)', color: 'white' }]}
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search glossary..."
          placeholderTextColor={'rgba(255,255,255,0.8)'}
        />
      </View>

      {/* Terms List */}
      <ScrollView style={styles.termsList} contentContainerStyle={styles.termsListContent}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={palette.primary || '#FF0600'} />
            <Text style={[styles.loadingText, { color: palette.mutedText }]}>
              <Text>Loading terms...</Text>
            </Text>
          </View>
        ) : filteredTerms.length === 0 ? (
          <Text style={[styles.emptyText, { color: palette.mutedText }]}>
            <Text>{searchQuery ? 'No terms found' : 'No terms available yet'}</Text>
          </Text>
        ) : (
          filteredTerms.map((term) => (
            <TouchableOpacity
              key={term.id}
              style={[
                styles.termCard,
              ]}
              onPress={() => setSelectedTerm(selectedTerm?.id === term.id ? null : term)}
            >
              <Text style={styles.termName}>
                <Text>{term.term}</Text>
              </Text>
              <Text style={styles.termDefinition}>
                <Text>{term.definition}</Text>
              </Text>
              
              {selectedTerm?.id === term.id && term.example && (
                <View style={styles.exampleBox}>
                  <Text style={styles.exampleLabel}>
                    <Text>Example:</Text>
                  </Text>
                  <Text style={styles.exampleText}>
                    <Text>{term.example}</Text>
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          ))
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 16,
    minHeight: 0,
  },
  header: {
    padding: 20,
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  searchContainer: {
    paddingHorizontal: 0,
    marginBottom: 16,
  },
  searchInput: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    ...Platform.select({
      web: {
        outlineStyle: 'none', // Remove blue outline on focus
      },
    }),
  },
  termsList: {
    marginTop: 10,
    flex: 1,
  },
  termsListContent: {
    paddingHorizontal: 0,
    paddingBottom: 20,
    gap: 6,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 12,
  },
  loadingText: {
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  emptyText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    paddingVertical: 40,
  },
  letterGroup: {
    marginBottom: 20,
  },
  letterHeader: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  termCard: {
    padding: 16,
    borderRadius: 20,
    marginBottom: 12,
    backgroundColor: '#18B3B7',
        ...Platform.select({
            web: {
                boxShadow: '5px 7px 0px rgba(0,0,0,1)',
                cursor: 'pointer',
            },
        }),
  },
  termHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  termName: {
    fontSize: 24,
    fontFamily: 'Chivo',
    fontWeight: '500',
    color: 'white',
    marginBottom: 8,
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: '#000000',
        textShadowOffset: { width: 5, height: 7 },
        textShadowRadius: 0,
      },
    }),
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    borderWidth: 1,
  },
  categoryText: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  termDetails: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
  },
  termDefinition: {
    fontSize: 14,
    fontFamily: 'Chivo',
    lineHeight: 20,
    color: 'white',
        ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: '#000000',
        textShadowOffset: { width: 5, height: 7 },
        textShadowRadius: 0,
      },
    }),
  },
  exampleBox: {
    marginTop: 8,
    padding: 10,
    borderRadius: 6,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  exampleLabel: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'rgba(255,255,255,0.8)',
  },
  exampleText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontStyle: 'italic',
    lineHeight: 18,
    color: 'rgba(255,255,255,0.9)',
  },
});

export default GlossaryPanel;

